import { POSITION_ALREADY_EXISTS_COPY, WALLET_ALREADY_REGISTERED_COPY } from "@/pages/AddPosition/errorCopy";
import type {
    FindSlice,
    MultisigSummary,
    NavSlice,
    ReverifySlice,
    RewardsDestinationVariant,
    RewardsSlice,
    SourceSlice,
    SubmitReview,
    SubmitSlice,
    VerifySlice
} from "@/pages/AddPosition/steps";
import type { StepFlow } from "@/pages/AddPosition/useStepFlow";
import { SAME_AS_SOURCE_LABEL } from "@/pages/RewardDestinations/destinationInput";
import { MOCK_SAVED_DESTINATIONS } from "@/services/sim/mockData";

/**
 * Fixture `StepFlow` values for the Add a Position design-QA showcase
 * (`/showcase/add-position`). Each state is the object `useStepFlow` returns
 * at that moment of the flow, so the showcase renders the REAL layout
 * (`PositionWalkthroughLayout` → rail, frame, action bars, Back/Next) with no
 * component forks. Labels, disabled flags and messages mirror the hook's
 * derivations; strings the hook exports (errorCopy) are imported, the rest are
 * the hook's inline copy restated here. Dev-only: the showcase route is not in
 * production bundles.
 */

export interface AddPositionShowcaseState {
    id: string;
    group: "enroll" | "rewards" | "submit";
    title: string;
    caption: string;
    flow: StepFlow;
    rewardsVariant?: RewardsDestinationVariant;
}

const NO_OP = (): void => undefined;

// Stable, valid-looking base58 addresses (display only; nothing is resolved).
const SOURCE_WALLET = "7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU";
const OTHER_WALLET = "9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM";
const MULTISIG_ACCOUNT = "4aMSGsQuads8dTxw2rqV9dUk5Lq1nEqQpYz3Hc7GxWbA";
const MULTISIG_VAULT = "HvAu1tSquads5kM2uYbq8pRkF3aWnZt6rJxQdC9sLmNe";
const MEMBER_WALLET = "5MeMbErWa11etK9pQx2rS7tUvW3yZa4bC6dE8fGhJkLm";
const UNSUPPORTED_ADDRESS = "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA";

const SQUADS_BASE: MultisigSummary = {
    address: MULTISIG_ACCOUNT,
    vaultAddress: MULTISIG_VAULT,
    threshold: 2,
    memberCount: 3,
    members: [
        { address: MEMBER_WALLET, canVerify: true },
        { address: "8QrStUvWxYz1a2B3c4D5e6F7g8H9iJkLmNoPqRsTuVwX", canVerify: true },
        { address: "3ProPoSerOnLy7a8B9c1D2e3F4g5H6i7J8k9LmNoPqRs", canVerify: false }
    ],
    signerStatus: "no-signer"
};

/* ── Slice defaults — the untouched first frame of the flow ─────────── */

function source(overrides: Partial<SourceSlice> = {}): SourceSlice {
    return {
        kind: null,
        connectedWalletAddress: null,
        multisigSummary: null,
        address: "",
        addressTouched: false,
        onAddressChange: NO_OP,
        onAddressBlur: NO_OP,
        addressLabel: "Address",
        addressPlaceholder: "Paste a wallet, Squads multisig, or vault address",
        addressInvalid: false,
        addressError: null,
        alreadyRegistered: false,
        showRetry: false,
        onRetry: NO_OP,
        notice: null,
        ...overrides
    };
}

function find(overrides: Partial<FindSlice> = {}): FindSlice {
    return {
        status: "idle",
        message: null,
        messageTone: "muted",
        addressPreview: null,
        foundPosition: null,
        ...overrides
    };
}

function verify(overrides: Partial<VerifySlice> = {}): VerifySlice {
    return {
        status: "idle",
        message: null,
        messageTone: "muted",
        actionLabel: "Verify Source",
        actionDisabled: true,
        directLabel: "Verify Directly with Solomon",
        isDirect: false,
        retryReady: false,
        onVerify: NO_OP,
        directDisabled: true,
        onVerifyDirect: NO_OP,
        ...overrides
    };
}

function rewards(overrides: Partial<RewardsSlice> = {}): RewardsSlice {
    return {
        destination: "",
        onDestinationChange: NO_OP,
        defaultAddress: "",
        ...overrides
    };
}

/** A submit failure that isn't an expired proof: Submit Failed, then Retry. */
const SUBMIT_ERROR_SAMPLE = "We couldn't submit this position. Please try again.";

function reverify(overrides: Partial<ReverifySlice> = {}): ReverifySlice {
    return { status: "idle", retryReady: false, onVerifyAgain: NO_OP, ...overrides };
}

function submit(overrides: Partial<SubmitSlice> = {}): SubmitSlice {
    return {
        automaticEnrollment: true,
        onSubmit: NO_OP,
        isSubmitting: false,
        error: null,
        submitted: false,
        review: null,
        ...overrides
    };
}

function nav(overrides: Partial<NavSlice> = {}): NavSlice {
    return {
        active: 0,
        setActive: NO_OP,
        canAdvance: false,
        isLast: false,
        onNext: NO_OP,
        onBack: NO_OP,
        revealDelay: 0,
        ...overrides
    };
}

function flow(parts: Partial<StepFlow>): StepFlow {
    return {
        source: source(),
        find: find(),
        verify: verify(),
        rewards: rewards(),
        submit: submit(),
        nav: nav(),
        ...parts
    };
}

/* ── Reused composite slices ───────────────────────────────────────── */

/** EOA source, connected as that same wallet: Verify Ownership is live. */
const eoaMatchSource = source({
    kind: "eoa",
    address: SOURCE_WALLET,
    connectedWalletAddress: SOURCE_WALLET,
    addressTouched: true
});
const eoaReadyVerify = verify({
    actionLabel: "Verify Ownership",
    actionDisabled: false,
    directDisabled: false
});
const foundFunded = find({
    status: "found",
    foundPosition: { balanceLabel: "$31.57", zeroBalance: false }
});
const verifiedEoa = verify({
    status: "verified",
    actionLabel: "Verified",
    actionDisabled: true,
    directDisabled: true
});
const eoaRewards = rewards({ defaultAddress: SOURCE_WALLET });

/** A verified, funded EOA — the state every later step builds on. */
function verifiedEoaFlow(parts: Partial<StepFlow>): StepFlow {
    return flow({
        source: eoaMatchSource,
        find: foundFunded,
        verify: verifiedEoa,
        rewards: eoaRewards,
        ...parts
    });
}

/** A verified Squads source (member signed): rewards default to the vault. */
function verifiedSquadsFlow(parts: Partial<StepFlow>): StepFlow {
    return flow({
        source: source({
            kind: "multisig-squads",
            address: MULTISIG_ACCOUNT,
            addressTouched: true,
            connectedWalletAddress: MEMBER_WALLET,
            multisigSummary: { ...SQUADS_BASE, signerStatus: "can-verify" }
        }),
        find: foundFunded,
        verify: verifiedEoa,
        rewards: rewards({ defaultAddress: MULTISIG_VAULT }),
        ...parts
    });
}

const defaultReview: SubmitReview = {
    totalPositions: 1,
    totalBalanceLabel: "$31.57",
    rewardsDestinationAddress: SOURCE_WALLET,
    rewardsDestinationLabel: SAME_AS_SOURCE_LABEL
};

const SAVED_PICK = MOCK_SAVED_DESTINATIONS[0];

/** What `formatObservedUsdv` renders for an amount it cannot know. */
const UNOBSERVED = "\u2014";

/** Discovery found the Address holding plus LP positions in the GOLD / USDv
 *  pool: one row per surface and pool, the LP count under the pool name
 *  (`resolveHoldingsPreview`). The total sums the Address and LP USDv. */
const ADDRESS_ROW = {
    key: "surface-wallet:address",
    label: "USDv",
    surface: "Address",
    positionCount: 1,
    balanceLabel: "$31.57"
};
const GOLD_LP_ROW = {
    key: "surface-gold-lp:CpG1ZMHhK7iGKpvS1A6Ytf4hJbziY8xq1jKAX5GbfaZ2",
    label: "GOLD / USDv",
    surface: "Raydium CLMM",
    positionCount: 2,
    balanceLabel: "$842.16"
};
const foundWithLp = find({
    status: "found",
    foundPosition: {
        balanceLabel: "$873.73",
        zeroBalance: false,
        totalPositions: 3,
        rows: [ADDRESS_ROW, GOLD_LP_ROW]
    }
});
const lpReview: SubmitReview = {
    ...defaultReview,
    totalPositions: 3,
    totalBalanceLabel: "$873.73"
};

/* ── The states ────────────────────────────────────────────────────── */

export const ADD_POSITION_SHOWCASE_STATES: AddPositionShowcaseState[] = [
    // Step 1 — Add Address
    {
        id: "enroll-empty",
        group: "enroll",
        title: "Empty field",
        caption: "First paint. No wallet connected, nothing pasted: the action reads Verify Source, disabled.",
        flow: flow({})
    },
    {
        id: "enroll-resolving",
        group: "enroll",
        title: "Address resolving",
        caption: 'A pasted address is being classified. The line under the field reads "Checking address...".',
        flow: flow({
            source: source({ address: SOURCE_WALLET, notice: "Checking address..." })
        })
    },
    {
        id: "enroll-discovering",
        group: "enroll",
        title: "Address balance shown, discovery in flight",
        caption:
            "Discovery runs on its own once the address is classified, no wallet needed. The live Address USDv appears first (after at most 1.2s of Checking address...); LP holdings join the card when discovery settles.",
        flow: flow({
            source: source({ kind: "eoa", address: SOURCE_WALLET, addressTouched: true }),
            find: find({ status: "finding", addressPreview: { balanceLabel: "$31.57", zeroBalance: false } }),
            verify: verify({ actionLabel: "Connect Wallet", actionDisabled: false, directDisabled: false })
        })
    },
    {
        id: "enroll-lp-discovered",
        group: "enroll",
        title: "LP positions discovered, before verification",
        caption:
            "Discovery found GOLD / USDv LP positions on the address: a USDv / Address row, then GOLD / USDv with Raydium CLMM (2 LP positions) under it. Shown before any wallet connects; Next still waits for verification.",
        flow: flow({
            source: source({ kind: "eoa", address: SOURCE_WALLET, addressTouched: true }),
            find: foundWithLp,
            verify: verify({ actionLabel: "Connect Wallet", actionDisabled: false, directDisabled: false })
        })
    },
    {
        id: "enroll-balance-unavailable",
        group: "enroll",
        title: "Address balance unavailable",
        caption:
            "The live USDv read errored: the USDv row shows an em dash, never $0.00. The address can still be verified.",
        flow: flow({
            source: source({ kind: "eoa", address: SOURCE_WALLET, addressTouched: true }),
            find: find({ addressPreview: { balanceLabel: UNOBSERVED, zeroBalance: false } }),
            verify: verify({ actionLabel: "Connect Wallet", actionDisabled: false, directDisabled: false })
        })
    },
    {
        id: "enroll-invalid",
        group: "enroll",
        title: "Invalid address",
        caption: "Not a Solana address. The field takes the error rail with its message under it.",
        flow: flow({
            source: source({
                address: "not-a-solana-address",
                addressTouched: true,
                addressInvalid: true,
                addressError: "This isn't a valid address."
            })
        })
    },
    {
        id: "enroll-unsupported",
        group: "enroll",
        title: "Unsupported type: direct verification available",
        caption:
            "A valid address that is neither a wallet nor a Squads account (here, a program). No error line and no type badge: Verify Source stays disabled and Verify Directly with Solomon is the way forward.",
        flow: flow({
            source: source({ address: UNSUPPORTED_ADDRESS, addressTouched: true }),
            verify: verify({ directDisabled: false })
        })
    },
    {
        id: "enroll-unsupported-version",
        group: "enroll",
        title: "Unsupported multisig version",
        caption:
            "A multisig that is not Squads V4 renders the same as an unsupported type: no error line, Verify Source disabled, Verify Directly with Solomon live.",
        flow: flow({
            source: source({ address: MULTISIG_ACCOUNT, addressTouched: true }),
            verify: verify({ directDisabled: false })
        })
    },
    {
        id: "enroll-lookup-failed",
        group: "enroll",
        title: "Address lookup failed",
        caption:
            'Classification could not reach the network: "Address check failed." with a Retry link under the field.',
        flow: flow({
            source: source({ address: MULTISIG_ACCOUNT, addressTouched: true, showRetry: true })
        })
    },
    {
        id: "enroll-eoa-no-wallet",
        group: "enroll",
        title: "Wallet detected, not connected",
        caption:
            "The wallet's USDv balance appears before verification. No wallet connected, so the action reads Connect Wallet.",
        flow: flow({
            source: source({ kind: "eoa", address: SOURCE_WALLET, addressTouched: true }),
            find: find({ addressPreview: { balanceLabel: "$31.57", zeroBalance: false } }),
            verify: verify({ actionLabel: "Connect Wallet", actionDisabled: false, directDisabled: false })
        })
    },
    {
        id: "enroll-eoa-mismatch",
        group: "enroll",
        title: "Connected wallet mismatch",
        caption:
            "A different wallet is connected: Switch Wallet. Small error hint under the field, one sentence: the connected wallet is an Address in the sentence's own face and colour, inline, shortening in the middle only when it would not fit on a line.",
        flow: flow({
            source: source({
                kind: "eoa",
                address: SOURCE_WALLET,
                connectedWalletAddress: OTHER_WALLET,
                addressTouched: true
            }),
            verify: verify({ actionLabel: "Switch Wallet", actionDisabled: false, directDisabled: false })
        })
    },
    {
        id: "enroll-eoa-match",
        group: "enroll",
        title: "Connected wallet matches",
        caption:
            "The source wallet is connected: the field takes the success rail with a Connected status badge inside it, no hint line. Verify Ownership is live.",
        flow: flow({ source: eoaMatchSource, verify: eoaReadyVerify })
    },
    {
        id: "enroll-verifying",
        group: "enroll",
        title: "Verifying",
        caption:
            "Discovery then the wallet signature are in flight: Verifying..., pending light-bar, both actions inert.",
        flow: flow({
            source: eoaMatchSource,
            find: find({ status: "finding" }),
            verify: verify({ status: "verifying", actionLabel: "Verifying...", actionDisabled: true })
        })
    },
    {
        id: "enroll-micro-transfer",
        group: "enroll",
        title: "Verifying by test transfer",
        caption:
            "The wallet could not sign a message, so verification falls back to a tiny onchain transfer. The line under the field replaces the type badge while it runs.",
        flow: flow({
            source: {
                ...eoaMatchSource,
                notice: "Signing not possible. Attempting to verify through a tiny onchain transfer..."
            },
            find: foundFunded,
            verify: verify({ status: "verifying", actionLabel: "Verifying...", actionDisabled: true })
        })
    },
    {
        id: "enroll-failed",
        group: "enroll",
        title: "Verify failed",
        caption: "The signature failed or was rejected: Verification Failed for the 3s hold, error light-bar.",
        flow: flow({
            source: eoaMatchSource,
            verify: verify({
                status: "failed",
                actionLabel: "Verification Failed",
                actionDisabled: true,
                directDisabled: false
            })
        })
    },
    {
        id: "enroll-retry",
        group: "enroll",
        title: "Verify failed, retry ready",
        caption: "After the hold the button becomes an enabled Retry with the retry icon.",
        flow: flow({
            source: eoaMatchSource,
            verify: verify({
                status: "failed",
                retryReady: true,
                actionLabel: "Retry",
                actionDisabled: false,
                directDisabled: false
            })
        })
    },
    {
        id: "enroll-verify-error",
        group: "enroll",
        title: "Verify failed with a reason",
        caption:
            "The ownership challenge was rejected with a specific code (here WALLET_ALREADY_REGISTERED): the reason sits beside the actions in the error tone, the button holds Verification Failed, then Retry.",
        flow: flow({
            source: eoaMatchSource,
            find: foundFunded,
            verify: verify({
                status: "failed",
                message:
                    "This wallet already belongs to another company recipient. Use a different wallet or contact Solomon support.",
                messageTone: "error",
                actionLabel: "Verification Failed",
                actionDisabled: true,
                directDisabled: false
            })
        })
    },
    {
        id: "enroll-verified-found",
        group: "enroll",
        title: "Verified, position found",
        caption:
            "Ownership proven and the holding discovered: Positions / Balance card (p-3 inside), as wide as the field. Next is enabled.",
        flow: verifiedEoaFlow({ nav: nav({ canAdvance: true }) })
    },
    {
        id: "enroll-verified-zero",
        group: "enroll",
        title: "Verified, zero balance",
        caption: "An empty wallet is still registerable: the same Positions / Balance card, USDv at $0.00.",
        flow: verifiedEoaFlow({
            find: find({ status: "found", foundPosition: { balanceLabel: "$0.00", zeroBalance: true } }),
            nav: nav({ canAdvance: true })
        })
    },
    {
        id: "enroll-verified-lp",
        group: "enroll",
        title: "Verified, Address and LP positions found",
        caption:
            "Ownership proven on an address holding USDv and two GOLD / USDv LP positions: both rows in the card, the field takes Verified. Next is enabled.",
        flow: verifiedEoaFlow({ find: foundWithLp, nav: nav({ canAdvance: true }) })
    },
    {
        id: "enroll-lp-unobserved",
        group: "enroll",
        title: "LP balance not yet observed",
        caption:
            "An LP observation is incomplete: its row reads an em dash, and so does the total carried to the review. The Address row keeps its live balance.",
        flow: verifiedEoaFlow({
            find: find({
                status: "found",
                foundPosition: {
                    balanceLabel: UNOBSERVED,
                    zeroBalance: false,
                    totalPositions: 3,
                    incomplete: true,
                    rows: [ADDRESS_ROW, { ...GOLD_LP_ROW, balanceLabel: UNOBSERVED }]
                }
            }),
            nav: nav({ canAdvance: true })
        })
    },
    {
        id: "enroll-not-covered",
        group: "enroll",
        title: "Not covered",
        caption:
            "The account has no active Program with an Address surface: hard stop, contact the distributor. To be deprecated once addresses register independently of Programs.",
        flow: flow({
            source: eoaMatchSource,
            find: find({
                status: "not-covered",
                message: "Your account needs to be set up before this address can be added. Please contact Solomon.",
                messageTone: "error"
            }),
            verify: verify({
                status: "failed",
                actionLabel: "Verification Failed",
                actionDisabled: true,
                directDisabled: false
            })
        })
    },
    {
        id: "enroll-already-registered",
        group: "enroll",
        title: "Already registered",
        caption:
            "The pasted wallet already has a position (any surface), known as soon as it is classified: no wallet, no verify click. The field takes the error rail and an Already Registered badge sits beside Wallet. Both verify actions are disabled.",
        flow: flow({
            source: source({ kind: "eoa", address: SOURCE_WALLET, addressTouched: true, alreadyRegistered: true }),
            verify: verify({ actionLabel: "Connect Wallet", actionDisabled: true, directDisabled: true })
        })
    },
    {
        id: "enroll-already-registered-discovery",
        group: "enroll",
        title: "Already registered, found at discovery",
        caption:
            "The backstop: discovery rejects a duplicate the source check missed (the positions list loaded late). Renders the same, error rail plus Already Registered badge, no Connected badge, both actions disabled.",
        flow: flow({
            source: { ...eoaMatchSource, alreadyRegistered: true },
            find: find({ status: "already-registered" }),
            verify: verify({ actionLabel: "Verify Ownership", actionDisabled: true, directDisabled: true })
        })
    },
    {
        id: "enroll-discovery-failed",
        group: "enroll",
        title: "Discovery failed",
        caption:
            'The discovery call errored after Verify: Verification Failed, then Retry (re-runs discovery), with "Address check failed." under the field.',
        flow: flow({
            source: eoaMatchSource,
            find: find({
                status: "failed",
                message: "Address check failed.",
                messageTone: "error"
            }),
            verify: verify({
                status: "failed",
                actionLabel: "Verification Failed",
                actionDisabled: true,
                directDisabled: false
            })
        })
    },
    {
        id: "enroll-direct-selected",
        group: "enroll",
        title: "Direct verification selected",
        caption:
            "Solomon will verify out of band: the link reads Direct Verification Selected, the field shows Pending Verification, the found card shows, Next is enabled.",
        flow: verifiedEoaFlow({
            verify: verify({
                status: "verified",
                isDirect: true,
                manualReview: {
                    description: "Our custom treasury contract holds the funds.",
                    onDescriptionChange: NO_OP
                },
                actionLabel: "Verify Ownership",
                actionDisabled: true,
                directDisabled: true
            }),
            nav: nav({ canAdvance: true })
        })
    },
    {
        id: "enroll-squads-no-wallet",
        group: "enroll",
        title: "Squads multisig resolved, no wallet",
        caption:
            'Squads Account (2/3) badge (threshold / signers); the signer tooltip lists authorised members. No wallet: Connect Member Wallet. (The hook\'s "Resolved 3 signers" line is muted-tone and filtered out.)',
        flow: flow({
            source: source({
                kind: "multisig-squads",
                address: MULTISIG_ACCOUNT,
                addressTouched: true,
                multisigSummary: SQUADS_BASE
            }),
            verify: verify({ actionLabel: "Connect Member Wallet", actionDisabled: false, directDisabled: false })
        })
    },
    {
        id: "enroll-squads-vault",
        group: "enroll",
        title: "Squads vault pasted",
        caption:
            "The vault address was pasted instead of the account: the badge reads Squads Vault (2/3), the same multisig and signer tooltip.",
        flow: flow({
            source: source({
                kind: "multisig-squads",
                address: MULTISIG_VAULT,
                addressTouched: true,
                multisigSummary: SQUADS_BASE
            }),
            verify: verify({ actionLabel: "Connect Member Wallet", actionDisabled: false, directDisabled: false })
        })
    },
    {
        id: "enroll-squads-already-registered",
        group: "enroll",
        title: "Squads, already registered",
        caption:
            "The multisig account (the key the API uses, whether the account or its vault was pasted) already has a position: error rail, Already Registered beside the Squads badge, actions disabled.",
        flow: flow({
            source: source({
                kind: "multisig-squads",
                address: MULTISIG_VAULT,
                addressTouched: true,
                multisigSummary: SQUADS_BASE,
                alreadyRegistered: true
            }),
            verify: verify({ actionLabel: "Connect Member Wallet", actionDisabled: true, directDisabled: true })
        })
    },
    {
        id: "enroll-squads-member",
        group: "enroll",
        title: "Squads, member connected",
        caption:
            'A member with Vote or Execute is connected: a green Valid Signer badge, then "[wallet] connected." in small (the field holds the multisig, so no Connected badge). Verify Ownership.',
        flow: flow({
            source: source({
                kind: "multisig-squads",
                address: MULTISIG_ACCOUNT,
                addressTouched: true,
                connectedWalletAddress: MEMBER_WALLET,
                multisigSummary: { ...SQUADS_BASE, signerStatus: "can-verify" }
            }),
            verify: eoaReadyVerify
        })
    },
    {
        id: "enroll-squads-not-member",
        group: "enroll",
        title: "Squads, not a member",
        caption:
            'The connected wallet is not a member: a red Not a Signer badge, then "[wallet] connected. Switch to a valid signer." in small. Switch Wallet.',
        flow: flow({
            source: source({
                kind: "multisig-squads",
                address: MULTISIG_ACCOUNT,
                addressTouched: true,
                connectedWalletAddress: OTHER_WALLET,
                multisigSummary: { ...SQUADS_BASE, signerStatus: "not-member" }
            }),
            verify: verify({ actionLabel: "Switch Wallet", actionDisabled: false, directDisabled: false })
        })
    },

    {
        id: "enroll-squads-cannot-verify",
        group: "enroll",
        title: "Squads, member without Vote or Execute",
        caption:
            'The connected wallet is a proposer-only member: an error hint, "[wallet] connected. This member can\'t verify (needs Vote or Execute). Switch to an authorized member wallet." Switch Wallet.',
        flow: flow({
            source: source({
                kind: "multisig-squads",
                address: MULTISIG_ACCOUNT,
                addressTouched: true,
                connectedWalletAddress: "3ProPoSerOnLy7a8B9c1D2e3F4g5H6i7J8k9LmNoPqRs",
                multisigSummary: { ...SQUADS_BASE, signerStatus: "cannot-verify" }
            }),
            verify: verify({ actionLabel: "Switch Wallet", actionDisabled: false, directDisabled: false })
        })
    },
    {
        id: "enroll-squads-signers-unavailable",
        group: "enroll",
        title: "Squads, signer list unavailable",
        caption:
            'The multisig resolved without its member list. Hover the badge tooltip: "Signer list unavailable; membership will be checked again on submit."',
        flow: flow({
            source: source({
                kind: "multisig-squads",
                address: MULTISIG_ACCOUNT,
                addressTouched: true,
                multisigSummary: {
                    address: MULTISIG_ACCOUNT,
                    vaultAddress: MULTISIG_VAULT,
                    threshold: 2,
                    memberCount: 3,
                    signerStatus: "no-signer"
                }
            }),
            verify: verify({ actionLabel: "Connect Member Wallet", actionDisabled: false, directDisabled: false })
        })
    },

    // Step 2 — Rewards Destination (Optional)
    {
        id: "rewards-locked",
        group: "rewards",
        title: "Locked (not used today)",
        caption:
            "The locked variant: read-only destination, heading Set Rewards Destination Address (Coming Soon). The console uses the saved picker.",
        rewardsVariant: "locked",
        flow: verifiedEoaFlow({ nav: nav({ active: 1, canAdvance: true }) })
    },
    {
        id: "rewards-saved-empty",
        group: "rewards",
        title: "Saved picker, nothing chosen",
        caption:
            "The saved Rewards Destinations picker, empty: rewards go to the position source. Options come from sim data, one line each: label, then the address shortening in the middle to the room the label leaves. Typing what matches nothing leaves one row, Add a new destination address (+ at the right): it adds a valid Solana address, and stays disabled, with why on hover, for anything else.",
        rewardsVariant: "saved",
        flow: verifiedEoaFlow({ nav: nav({ active: 1, canAdvance: true }) })
    },
    {
        id: "rewards-saved-chosen",
        group: "rewards",
        title: "Saved picker, destination chosen",
        caption: `A saved destination is picked (${SAVED_PICK.name}).`,
        rewardsVariant: "saved",
        flow: verifiedEoaFlow({
            rewards: rewards({ defaultAddress: SOURCE_WALLET, destination: SAVED_PICK.address }),
            nav: nav({ active: 1, canAdvance: true })
        })
    },
    {
        id: "rewards-single",
        group: "rewards",
        title: "Single custom destination",
        caption:
            "Not reachable today: built, but no flag selects it (REWARDS_DESTINATION_VARIANT is saved or locked). Shown pinned for reference.",
        rewardsVariant: "single",
        flow: verifiedEoaFlow({ nav: nav({ active: 1, canAdvance: true }) })
    },

    {
        id: "rewards-saved-squads",
        group: "rewards",
        title: "Saved picker, Squads source",
        caption:
            "A Squads source looks like any other: the default option reads Multisig Vault with the vault address (never the multisig account). Typing the multisig account picks that default; adding any other multisig account saves its vault.",
        rewardsVariant: "saved",
        flow: verifiedSquadsFlow({
            rewards: rewards({ defaultAddress: MULTISIG_VAULT, destination: "" }),
            nav: nav({ active: 1, canAdvance: true })
        })
    },
    {
        id: "rewards-single-squads",
        group: "rewards",
        title: "Single custom destination, Squads source",
        caption:
            "Not reachable today (no flag selects single). Same as any source: the field defaults to the vault (Multisig Vault placeholder); typing the multisig account resolves to that default.",
        rewardsVariant: "single",
        flow: verifiedSquadsFlow({
            rewards: rewards({ defaultAddress: MULTISIG_VAULT, destination: "" }),
            nav: nav({ active: 1, canAdvance: true })
        })
    },

    // Step 3 — Submit
    {
        id: "submit-review",
        group: "submit",
        title: "Review, automatic enrollment",
        caption:
            "The Review heading, then flat read-only fields (Address, Discovered Positions, Total USDv, Destination, Destination Label; the Address summary above folds into it) and the in-box Add Address action, no note. Footer Next is disabled on the last step.",
        flow: verifiedEoaFlow({
            submit: submit({ review: defaultReview }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-review-lp",
        group: "submit",
        title: "Review, Address and LP positions",
        caption:
            "Discovery found the Address holding and two GOLD / USDv LP positions: Discovered Positions reads 3 and Total USDv sums the Address and LP USDv.",
        flow: verifiedEoaFlow({
            find: foundWithLp,
            submit: submit({ review: lpReview }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-review-lp-unobserved",
        group: "submit",
        title: "Review, LP balance not yet observed",
        caption: "An incomplete LP observation carries through: Total USDv reads an em dash.",
        flow: verifiedEoaFlow({
            submit: submit({ review: { ...lpReview, totalBalanceLabel: UNOBSERVED } }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-review-manual",
        group: "submit",
        title: "Review, manual approval",
        caption:
            "A surface without automatic enrollment, or any direct verification: the action reads Submit Address for Review.",
        flow: verifiedEoaFlow({
            submit: submit({ automaticEnrollment: false, review: defaultReview }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-review-saved",
        group: "submit",
        title: "Review, saved destination",
        caption:
            "The Destination field shows the saved label, then its address shortening in the middle to the room left.",
        flow: verifiedEoaFlow({
            submit: submit({
                review: {
                    ...defaultReview,
                    rewardsDestinationAddress: SAVED_PICK.address,
                    rewardsDestinationLabel: SAVED_PICK.name
                }
            }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-review-unlabeled",
        group: "submit",
        title: "Review, unlabeled destination",
        caption:
            "A destination with no label: the Destination field is the address alone, in full when it fits, shortening in the middle when it doesn't.",
        flow: verifiedEoaFlow({
            submit: submit({
                review: {
                    ...defaultReview,
                    rewardsDestinationAddress: SAVED_PICK.address,
                    rewardsDestinationLabel: null
                }
            }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-submitting",
        group: "submit",
        title: "Adding",
        caption:
            "Create-position request in flight (automatic enrollment): Adding..., pending light-bar. A manual-review submission reads Submitting.",
        flow: verifiedEoaFlow({
            submit: submit({ isSubmitting: true, review: defaultReview }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-error",
        group: "submit",
        title: "Submit error",
        caption:
            "The request failed: Submit Failed for 3s, then Retry, which resubmits. No message line, for any reason.",
        flow: verifiedEoaFlow({
            submit: submit({ error: SUBMIT_ERROR_SAMPLE, review: defaultReview }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-already-registered",
        group: "submit",
        title: "Registered in the meantime",
        caption:
            "Rare: another member added this address after it was checked at entry. No error line: Already Registered for 3s, then Add New Address (long arrow), which clears the address and returns to step 1.",
        flow: verifiedEoaFlow({
            submit: submit({
                error: POSITION_ALREADY_EXISTS_COPY,
                review: defaultReview,
                reverify: reverify({ reason: "submit-failed" })
            }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-registered-elsewhere",
        group: "submit",
        title: "Registered to another company",
        caption:
            "The address belongs to another company recipient: like Registered in the meantime, Already Registered for 3s, then Add New Address back to step 1. No message line.",
        flow: verifiedEoaFlow({
            submit: submit({
                automaticEnrollment: false,
                error: WALLET_ALREADY_REGISTERED_COPY,
                review: defaultReview
            }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-failed-reverify",
        group: "submit",
        title: "Submit failed, proof may be consumed",
        caption:
            "A wallet-verified submit failed after the server may have used the proof: Submit Failed for 3s, then Verify Again (retry icon), which re-verifies in place before a resubmit. No message line.",
        flow: verifiedEoaFlow({
            submit: submit({
                error: "This signature has already been used. Sign a fresh message.",
                review: defaultReview,
                reverify: reverify({ reason: "submit-failed" })
            }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-challenge-expired",
        group: "submit",
        title: "Ownership check expired",
        caption:
            "The signed challenge is past its expiry (or was issued in another client context): no message, the action says it: Verification Expired for 3s, then Verify Again.",
        flow: verifiedEoaFlow({
            submit: submit({
                review: defaultReview,
                reverify: reverify()
            }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-expired",
        group: "submit",
        title: "Verification expired",
        caption:
            "The test-transfer proof is past its 15 minutes: no message, the action says it: Verification Expired for 3s, then Verify Again (retry icon after the label), which re-verifies here, keeping the address, destination and review. No trip back to step 1.",
        flow: verifiedEoaFlow({
            submit: submit({ review: defaultReview, reverify: reverify() }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-expired-verifying",
        group: "submit",
        title: "Verifying again",
        caption:
            "Verify Again pressed: the same ceremony as step 1 (sign, or the test transfer), pending light-bar. On success the fresh proof replaces the old one and the action is Add Address again; it does not submit on its own.",
        flow: verifiedEoaFlow({
            submit: submit({
                review: defaultReview,
                reverify: reverify({ status: "verifying" })
            }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-expired-failed",
        group: "submit",
        title: "Verifying again failed",
        caption: "The re-verification failed: Verification Failed, then Retry after 3s, which re-verifies again.",
        flow: verifiedEoaFlow({
            submit: submit({
                review: defaultReview,
                reverify: reverify({ status: "failed" })
            }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-pending-review",
        group: "submit",
        title: "Added: pending issuer review",
        caption:
            'A direct-verification submit replaces the whole layout: the illustration, "Your position has been added for review", "Solomon will contact you shortly to verify your control of this position. Your position will be activated after approval.", and Continue back to positions.',
        flow: verifiedEoaFlow({
            submit: submit({
                submitted: true,
                pendingReview: {
                    uuid: "manual-review-position",
                    address: SOURCE_WALLET,
                    description: "Our custom treasury contract holds the funds.",
                    issuerName: "Solomon"
                }
            }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-success",
        group: "submit",
        title: "Added, automatic enrollment",
        caption:
            "Added on the success light-bar, no message. In the app the host navigates to the positions table 3s later; here it stays put.",
        flow: verifiedEoaFlow({
            submit: submit({ submitted: true, review: defaultReview }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-success-lp",
        group: "submit",
        title: "Added, Address and LP positions",
        caption:
            "Added with LP positions discovered, no message: the LP positions land in the inventory once reconciliation confirms them.",
        flow: verifiedEoaFlow({
            find: foundWithLp,
            submit: submit({ submitted: true, review: lpReview }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    },
    {
        id: "submit-success-manual",
        group: "submit",
        title: "Submitted, manual approval",
        caption:
            "A surface without automatic enrollment (wallet-verified): Submitted on the success light-bar, no message. The host still moves on 3s later.",
        flow: verifiedEoaFlow({
            submit: submit({ automaticEnrollment: false, submitted: true, review: defaultReview }),
            nav: nav({ active: 2, canAdvance: true, isLast: true })
        })
    }
];
